import React, { useState } from 'react';

import { Typography } from '@mui/material';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { PendingInteractionCardChrome } from '../PendingInteractionCardChrome';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  columnsHeaderRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    minHeight: 28,
  },
  planHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  planTitle: {
    ...getTypographyForSx(theme.typography.subtitle2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 700,
  },
  section: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1, 0),
  },
  sectionBody: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
  },
  sectionLabel: {
    ...getTypographyForSx(theme.typography.caption),
    color: theme.appColors.iconsType,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
}));

interface QuestionPlanApprovalCardProps {
  /** The question list, usually a `QuestionColumnsEditor`. */
  editor: React.ReactNode;
  /** Usually `QuestionPlanActions`. */
  footer: React.ReactNode;
  /** Label above the question list: "Columns" on a report, "Questions" on a run. */
  listLabel: string;
  title: string;
  /** Above the list: a report's name and goal, or what the run is for. */
  header?: React.ReactNode;
  /** Right of the list label: filters and the matching document count. */
  listActions?: React.ReactNode;
  maxHeight?: number | null;
  /** Below the list: usually a `RunSummary`. */
  summary?: React.ReactNode;
}

/**
 * The card a user decides a question plan on, whether the plan creates a
 * report or runs questions to answer a chat question. Both cards share this
 * frame, the question list and the actions, so creating and running
 * questions looks and works the same in either place.
 */
export const QuestionPlanApprovalCard = React.memo(function QuestionPlanApprovalCard({
  editor,
  footer,
  header,
  listActions,
  listLabel,
  maxHeight,
  summary,
  title,
}: QuestionPlanApprovalCardProps) {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(true);

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={footer}
      maxHeight={maxHeight}
      title={title}
      toggleLabel="approval"
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <div className={classes.body}>
        {header}
        <div className={classes.columnsHeaderRow}>
          <Typography className={classes.sectionLabel}>{listLabel}</Typography>
          {listActions}
        </div>
        {editor}
        {summary}
      </div>
    </PendingInteractionCardChrome>
  );
});

/** A plan's title, if it has one, and the sentence under it. */
export const QuestionPlanHeader = React.memo(function QuestionPlanHeader({
  description,
  title,
}: {
  description: string;
  title?: string;
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.planHeader}>
      {Boolean(title) && <Typography className={classes.planTitle}>{title}</Typography>}
      <Typography className={classes.sectionBody}>{description}</Typography>
    </div>
  );
});

/** A labelled block of plan prose, such as a report's "How to use". */
export const QuestionPlanSection = React.memo(function QuestionPlanSection({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
}) {
  const { classes } = useStyles();
  return (
    <div className={classes.section}>
      <Typography className={classes.sectionLabel}>{label}</Typography>
      <Typography className={classes.sectionBody}>{children}</Typography>
    </div>
  );
});
